/**
 * @fileoverview Appointment type classification (MOR-470, MOR-1838)
 *
 * Purpose: one source of truth for which appointment types are personal
 * insolvency (an individual, not a company) and which of those are
 * bankruptcies. Shared by the Convex backend and the frontend so a new
 * appointment type only needs classifying here.
 *
 * - Personal insolvency: every appointment over an individual's estate —
 *   the legacy `personal_bankruptcy` / `business_bankruptcy` values plus the
 *   MOR-1838 Creditors Petition, Debtors Petition and Part X types.
 * - Bankruptcy: the subset where the individual is a bankrupt, so the
 *   Bankruptcy Act estate machinery applies (s 115 commencement, the
 *   s 120–122 claw-back windows). A Part X personal insolvency agreement is
 *   personal insolvency but NOT a bankruptcy — the debtor is never made
 *   bankrupt unless the arrangement fails.
 *
 * Used by: key-date derivations, the conflict brief, agents that switch to
 * the individual (Bankruptcy Act) rule set, and the create-job/settings UI.
 */

import type { AppointmentType, BankruptcyPetitionType } from "@cvx/jobs/schema";

/**
 * Appointment types over an individual's estate, in picker order: the
 * MOR-1838 types first, then the legacy generic values existing jobs carry.
 */
export const PERSONAL_INSOLVENCY_APPOINTMENT_TYPES = [
  "creditors_petition",
  "debtors_petition",
  "part_x_arrangement",
  "personal_bankruptcy",
  "business_bankruptcy",
] as const satisfies readonly AppointmentType[];

/** Personal insolvency appointments in which the individual is a bankrupt. */
export const BANKRUPTCY_APPOINTMENT_TYPES = [
  "personal_bankruptcy",
  "business_bankruptcy",
  "creditors_petition",
  "debtors_petition",
] as const satisfies readonly AppointmentType[];

export type PersonalInsolvencyAppointmentType =
  (typeof PERSONAL_INSOLVENCY_APPOINTMENT_TYPES)[number];

/**
 * True for any appointment over an individual (bankruptcy or Part X).
 *
 * @example
 * isPersonalInsolvencyAppointmentType("part_x_arrangement"); // true
 * isPersonalInsolvencyAppointmentType("cvl"); // false
 */
export function isPersonalInsolvencyAppointmentType(
  type: string | null | undefined,
): type is PersonalInsolvencyAppointmentType {
  return (
    !!type &&
    (PERSONAL_INSOLVENCY_APPOINTMENT_TYPES as readonly string[]).includes(type)
  );
}

/**
 * True when the individual is a bankrupt — excludes Part X arrangements.
 *
 * @example
 * isBankruptcyAppointmentType("creditors_petition"); // true
 * isBankruptcyAppointmentType("part_x_arrangement"); // false
 */
export function isBankruptcyAppointmentType(
  type: string | null | undefined,
): boolean {
  return (
    !!type && (BANKRUPTCY_APPOINTMENT_TYPES as readonly string[]).includes(type)
  );
}

/**
 * An individual's estate rather than a company (MOR-1847). A pre-appointment
 * job may not have an appointment type yet; the bankrupt's name — only
 * captured on the personal New Job form — marks it as personal then.
 *
 * @example
 * isPersonalMatter({ appointmentType: "part_x_arrangement" }); // true
 * isPersonalMatter({ bankruptName: "Alex Example" }); // true (untyped pre-appointment)
 * isPersonalMatter({ appointmentType: "cvl", bankruptName: "Alex" }); // false
 */
export function isPersonalMatter(job: {
  /** Loosely typed so UI job shapes that hold the type as a string fit too. */
  appointmentType?: string | null;
  bankruptName?: string | null;
}): boolean {
  if (job.appointmentType) {
    return isPersonalInsolvencyAppointmentType(job.appointmentType);
  }
  return !!job.bankruptName?.trim();
}

/**
 * The petition type an appointment type implies, so a Creditors/Debtors
 * Petition job doesn't need the petition recorded a second time for the
 * s 115 commencement calculator. Undefined when the type doesn't say.
 *
 * @example
 * petitionTypeForAppointmentType("debtors_petition"); // "debtors_petition"
 * petitionTypeForAppointmentType("personal_bankruptcy"); // undefined
 */
export function petitionTypeForAppointmentType(
  type: AppointmentType | null | undefined,
): BankruptcyPetitionType | undefined {
  if (type === "creditors_petition") return "creditors_petition";
  if (type === "debtors_petition") return "debtors_petition";
  return undefined;
}
